/** Statistics-only requests. Never creates, reviews, starts or stops an Agent. */
import api from '@/lib/api';
import { getAccessToken } from '@/lib/auth';
import { getStoredCurrentEnvironment } from '@/lib/environment';
import { isAxiosError } from 'axios';

const PREFIX = '/quantum/business/forex';
const STORE = 'forex_statistics_owner_pending_v1';
export type OwnerOption = { owner_ref: string; name: string; status: string; selectable?: boolean; disabled_reason?: string | null };
export type PendingOwner = {
  version: 1; environmentId: string; actorId: string; creationKey: string;
  creationAction: 'create'|'create-and-validate'; assignmentKey: string;
  ownerRef: string; agentId?: string; createdAt: number; attempts: number;
  due: number; error?: string;
};
let memory: PendingOwner[] = [];
const busy = new Set<string>();

export function ownerScope(): { environmentId: string; actorId: string } | null {
  try {
    const token = getAccessToken();
    if (!token) return null;
    const raw = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(raw));
    const env = getStoredCurrentEnvironment();
    if (!claims.sub || !env?.id) return null;
    return { actorId: String(claims.sub), environmentId: env.id };
  } catch { return null; }
}
function sameScope(row: PendingOwner) {
  const scope = ownerScope();
  return !!scope && scope.actorId === row.actorId && scope.environmentId === row.environmentId;
}
function read(): PendingOwner[] {
  try {
    const rows: unknown = JSON.parse(localStorage.getItem(STORE) || '[]');
    if (Array.isArray(rows)) for (const row of rows.slice(0, 100)) {
      if (row?.version === 1 && typeof row.assignmentKey === 'string' && typeof row.creationKey === 'string'
        && typeof row.ownerRef === 'string' && typeof row.actorId === 'string' && typeof row.environmentId === 'string'
        && ['create', 'create-and-validate'].includes(row.creationAction)
        && Number.isFinite(row.createdAt) && Number.isFinite(row.attempts) && Number.isFinite(row.due)
        && !memory.some(item => item.assignmentKey === row.assignmentKey)) memory.push(row);
    }
  } catch { /* In-memory recovery remains available if storage is unavailable. */ }
  return memory;
}
function write() {
  try { localStorage.setItem(STORE, JSON.stringify(memory)); } catch { /* No trading dependency. */ }
  window.dispatchEvent(new Event('forex-owner-pending'));
}
export async function ownerOptions(signal?: AbortSignal): Promise<OwnerOption[]> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const collected = new Map<string, OwnerOption>();
    const cursors = new Set<string>();
    let cursor: string | null = null;
    try {
      do {
        const response = await api.get(`${PREFIX}/operator-owner-options`, {
          params: { scope: 'all', limit: 100, ...(cursor ? { cursor } : {}) }, timeout: 2000, signal,
        });
        const page = response.data.data as { items: OwnerOption[]; next_cursor?: string | null; total: number };
        for (const owner of page.items) collected.set(owner.owner_ref, owner);
        cursor = page.next_cursor || null;
        if (cursor && cursors.has(cursor)) throw new Error('OWNER_CURSOR_LOOP');
        if (cursor) cursors.add(cursor);
        if (!cursor && collected.size !== page.total) throw new Error('OWNER_CATALOG_INCOMPLETE');
      } while (cursor);
      return [...collected.values()];
    } catch (cause) {
      if (attempt === 0 && isAxiosError(cause) && cause.response?.data?.detail?.code === 'OWNER_CATALOG_CHANGED') continue;
      throw cause;
    }
  }
  throw new Error('OWNER_CATALOG_CHANGED');
}
export function notifyOwnerCatalogChanged() {
  const scope = ownerScope();
  if (!scope) return;
  window.dispatchEvent(new CustomEvent('forex-owner-catalog-changed', { detail: scope }));
  try { const channel = new BroadcastChannel('forex-owner-catalog'); channel.postMessage(scope); channel.close(); } catch { /* Focus and manual refresh remain available. */ }
}
export function prepareOwner(ownerRef: string, creationKey: string, startValidation: boolean): PendingOwner | null {
  try {
    const scope = ownerScope();
    if (!ownerRef || !scope) return null;
    const existing = read().find(row => sameScope(row) && row.creationKey === creationKey);
    if (existing) return existing; // A timed-out creation retains its original selection.
    const row: PendingOwner = { ...scope, version: 1, ownerRef, creationKey,
      creationAction: startValidation ? 'create-and-validate' : 'create', assignmentKey: crypto.randomUUID(),
      createdAt: Date.now(), attempts: 0, due: Date.now() + 30_000 };
    memory.push(row); write(); return row;
  } catch { return null; }
}
export function completeOwnerCreation(row: PendingOwner | null, agentId: string) {
  if (!row) return;
  row.agentId = agentId; row.attempts = 0; row.due = Date.now(); write();
  void runOwner(row);
}
export function pendingOwners() { return read().filter(sameScope); }
export function retryOwner(row: PendingOwner) {
  if (!sameScope(row) || Date.now() - row.createdAt > 7 * 86400_000) return;
  row.attempts = 0; row.due = Date.now(); row.error = undefined; write(); void runOwner(row);
}
export async function runOwner(row: PendingOwner) {
  if (!sameScope(row) || busy.has(row.assignmentKey) || row.attempts >= 4 || row.due > Date.now()) return;
  if (Date.now() - row.createdAt > 7 * 86400_000) { row.error = '记录已过期，请管理员在盈利大盘补齐'; row.attempts = 4; write(); return; }
  busy.add(row.assignmentKey);
  try {
    const headers = { 'X-Admin-Env-Id': row.environmentId, 'Idempotency-Key': row.assignmentKey };
    if (!row.agentId) {
      const found = await api.post(`${PREFIX}/operator-owner/resolve-creation`, {
        creation_key: row.creationKey, creation_action: row.creationAction,
      }, { headers, timeout: 2000 });
      row.agentId = found.data.data.agent_id; write();
    }
    if (!sameScope(row)) return;
    await api.post(`${PREFIX}/agents/${encodeURIComponent(row.agentId!)}/operator-owner/initialize`, {
      owner_ref: row.ownerRef, expected_revision: 0, creation_key: row.creationKey,
      creation_action: row.creationAction, reason: '创建智能体时指定统计团队',
    }, { headers, timeout: 2000 });
    memory = memory.filter(item => item.assignmentKey !== row.assignmentKey); write();
  } catch (cause) {
    const status = isAxiosError(cause) ? cause.response?.status : undefined;
    const code = isAxiosError(cause) ? cause.response?.data?.detail?.code : undefined;
    row.attempts += 1;
    if (status && status < 500 && !(status === 404 && !row.agentId)) row.attempts = 4;
    row.error = code === 'OWNER_ALREADY_INITIALIZED' ? '已有归属，请刷新盈利大盘核对；不会自动覆盖'
      : status === 403 ? '无首次归属权限，请管理员补齐' : !row.agentId ? '创建结果待确认，团队待补齐'
      : '团队待补齐；智能体创建及验证不受影响';
    row.due = Date.now() + [1000, 3000, 10000][Math.min(row.attempts - 1, 2)]; write();
  } finally { busy.delete(row.assignmentKey); }
}
